"use client";

import {
	DIRECTORY_STATE_VIEW_RESULT_CAP,
	PAGE_SIZE,
	type SessionFormat,
	SPECIALTIES,
	THERAPIST_DIRECTORY_MAX_PAGE,
} from "@klarify/constants";
import { useRouter, useSearchParams } from "@klarify/i18n/navigation";
import type {
	DirectoryFacetOptions,
	FeaturedDirectoryEntry,
} from "@klarify/types";
import { plural as linguiPlural } from "@lingui/core/macro";
import { Trans, useLingui } from "@lingui/react/macro";
import { Country, State } from "country-state-city";
import { ChevronDown, SlidersHorizontal, X } from "lucide-react";
import {
	type ReactNode,
	useCallback,
	useDeferredValue,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import { cn } from "../../lib/utils";
import { api } from "../../trpc/client";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "../ui/select";
import { FeaturedTherapistsRow } from "./featured-therapists-row";
import { SESSION_FORMAT_LIST } from "./shared";
import {
	TherapistDirectoryCard,
	type TherapistDirectoryEntry,
} from "./therapist-directory-card";
import {
	type FacetOption,
	TherapistDirectoryFacetPicker,
} from "./therapist-directory-facet-picker";
import {
	citiesForLocation,
	isSortOption,
	parseFilters,
	type SortOption,
	splitByDelivery,
	statesForCountry,
} from "./therapist-directory-filters";
import { TherapistDirectoryPagination } from "./therapist-directory-pagination";
import { TherapistDirectoryCardPlaceholderGrid } from "./therapist-directory-skeleton";

const SPECIALTY_OPTIONS: FacetOption[] = SPECIALTIES.map((s) => ({
	id: s.id,
	label: s.label,
}));

const FILTER_KEYS = [
	"country",
	"state",
	"city",
	"specialty",
	"format",
	"q",
] as const;

export const TherapistDirectory = ({
	entries,
	page,
	totalPages,
	totalCount,
	facetOptions,
	featured,
	aside,
}: {
	entries: TherapistDirectoryEntry[];
	page: number;
	totalPages: number;
	totalCount: number;
	facetOptions: DirectoryFacetOptions;
	/** Paid-placement cards for page 1; hidden in any filtered or sorted view. */
	featured?: FeaturedDirectoryEntry[];
	aside?: ReactNode;
}) => {
	const { t } = useLingui();
	const router = useRouter();
	const searchParams = useSearchParams();
	const params = useMemo(
		() => new URLSearchParams(searchParams?.toString() ?? ""),
		[searchParams],
	);
	const filters = useMemo(() => parseFilters(params), [params]);

	// Search input is locally controlled so typing doesn't churn the URL on
	// every keystroke. We sync to the URL on a deferred effect below, and we
	// remember the last value we WROTE to the URL in a ref. The URL→state
	// sync below skips when filters.query matches that ref — without it, an
	// in-flight router.replace from typing would race with the user's next
	// keystroke and reset the input (e.g. typing "a", backspacing, then the
	// pending URL update from "a" lands and Effect 1 re-adds it).
	const [queryDraft, setQueryDraft] = useState(filters.query);
	const lastWrittenQueryRef = useRef(filters.query);
	useEffect(() => {
		if (filters.query === lastWrittenQueryRef.current) {
			return;
		}
		lastWrittenQueryRef.current = filters.query;
		setQueryDraft(filters.query);
	}, [filters.query]);
	const deferredQuery = useDeferredValue(queryDraft);
	useEffect(() => {
		const trimmed = deferredQuery.trim();
		if (trimmed === filters.query) {
			return;
		}
		lastWrittenQueryRef.current = trimmed;
		const next = new URLSearchParams(params.toString());
		if (trimmed) {
			next.set("q", trimmed);
		} else {
			next.delete("q");
		}
		next.delete("page");
		router.replace(`/therapists?${next.toString()}`, { scroll: false });
	}, [deferredQuery, filters.query, params, router]);

	const replaceParam = useCallback(
		(mutator: (next: URLSearchParams) => void) => {
			const next = new URLSearchParams(params.toString());
			mutator(next);
			next.delete("page");
			const qs = next.toString();
			router.replace(qs ? `/therapists?${qs}` : "/therapists", {
				scroll: false,
			});
		},
		[params, router],
	);

	const onCountryChange = useCallback(
		(value: string | null) => {
			replaceParam((next) => {
				if (value) {
					next.set("country", value);
				} else {
					next.delete("country");
				}
				next.delete("state");
				next.delete("city");
			});
		},
		[replaceParam],
	);

	const onStateChange = useCallback(
		(value: string | null) => {
			replaceParam((next) => {
				if (value) {
					next.set("state", value);
				} else {
					next.delete("state");
				}
				next.delete("city");
			});
		},
		[replaceParam],
	);

	const onCityChange = useCallback(
		(value: string | null) => {
			replaceParam((next) => {
				if (value) {
					next.set("city", value);
				} else {
					next.delete("city");
				}
			});
		},
		[replaceParam],
	);

	const onSpecialtyChange = useCallback(
		(value: string[]) => {
			replaceParam((next) => {
				if (value.length === 0) {
					next.delete("specialty");
				} else {
					next.set("specialty", value.join(","));
				}
			});
		},
		[replaceParam],
	);

	const onFormatChange = useCallback(
		(value: string[]) => {
			replaceParam((next) => {
				if (value.length === 0) {
					next.delete("format");
				} else {
					next.set("format", value.join(","));
				}
			});
		},
		[replaceParam],
	);

	const toggleFormat = (id: SessionFormat) => {
		const current = filters.formats;
		const next = current.includes(id)
			? current.filter((f) => f !== id)
			: [...current, id];
		onFormatChange(next);
	};

	const clearAll = () => {
		// `lastWrittenQueryRef` isn't reset here — the URL→state sync effect
		// re-aligns it on the next render after replaceParam removes `q`, so
		// there's no race in practice.
		setQueryDraft("");
		replaceParam((next) => {
			for (const k of FILTER_KEYS) {
				next.delete(k);
			}
		});
	};

	const hasFilters =
		filters.country !== null ||
		filters.state !== null ||
		filters.city !== null ||
		filters.specialties.length > 0 ||
		filters.formats.length > 0 ||
		filters.query.length > 0;

	// Active filters NOT including the search query — these are the ones
	// behind the collapsible toggle. Used for both the count badge and the
	// initial expansion state (so a shared link with `?country=CA` opens
	// with the relevant controls already visible).
	const activeFilterCount =
		(filters.country ? 1 : 0) +
		(filters.state ? 1 : 0) +
		(filters.city ? 1 : 0) +
		filters.specialties.length +
		filters.formats.length;
	const [filtersExpanded, setFiltersExpanded] = useState(activeFilterCount > 0);

	const isStateView = filters.state !== null;
	// `filters.page <= 1` keeps legacy `?page=N` URLs working: the static
	// shell always carries page 1, so a deep query-param page must fetch.
	const isServerMode =
		!hasFilters && filters.sort === "recommended" && filters.page <= 1;

	const search = api.clinicRouter.profile.directorySearch.useQuery(
		{
			country: filters.country,
			state: filters.state,
			city: filters.city,
			specialties: filters.specialties,
			formats: filters.formats,
			q: filters.query,
			sort: filters.sort,
			page: isStateView ? 1 : filters.page,
			take: isStateView ? DIRECTORY_STATE_VIEW_RESULT_CAP : null,
		},
		{ enabled: !isServerMode, placeholderData: (prev) => prev },
	);

	const viewEntries = isServerMode ? entries : (search.data?.entries ?? []);
	const viewTotal = isServerMode ? totalCount : (search.data?.total ?? 0);
	const viewTotalPages = isServerMode
		? totalPages
		: Math.min(
				Math.max(1, Math.ceil(viewTotal / PAGE_SIZE)),
				THERAPIST_DIRECTORY_MAX_PAGE,
			);
	const viewPage = isServerMode ? page : filters.page;
	const isSearching = !isServerMode && search.isPending;
	const searchFailed = !isServerMode && search.isError;

	// A stale or crawled `?page=N` can exceed the filtered set's page count,
	// which would fetch an empty window. Correct the URL to the last real
	// page. Fires only while the clamp changes the value, so it cannot loop.
	useEffect(() => {
		if (isServerMode || !search.data) {
			return;
		}
		const maxPage = search.data.totalPages;
		if (filters.page <= maxPage) {
			return;
		}
		const next = new URLSearchParams(params.toString());
		if (maxPage <= 1) {
			next.delete("page");
		} else {
			next.set("page", String(maxPage));
		}
		const qs = next.toString();
		router.replace(qs ? `/therapists?${qs}` : "/therapists", {
			scroll: false,
		});
	}, [isServerMode, search.data, filters.page, params, router]);

	const split = useMemo(() => {
		const state = filters.state;
		return state && !isServerMode
			? splitByDelivery(viewEntries, state, filters.country)
			: null;
	}, [filters.state, isServerMode, viewEntries, filters.country]);

	const onSortChange = useCallback(
		(value: SortOption) => {
			replaceParam((next) => {
				if (value === "recommended") {
					next.delete("sort");
				} else {
					next.set("sort", value);
				}
			});
		},
		[replaceParam],
	);

	// Facet options come from the server, precomputed over the full published
	// corpus — they don't narrow when other filters are applied, so users can
	// broaden their search by changing location without clearing filters first.
	const countryOptions = useMemo<FacetOption[]>(
		() =>
			facetOptions.countries
				.map((id) => ({
					id,
					label: Country.getCountryByCode(id)?.name ?? id,
				}))
				.sort((a, b) => a.label.localeCompare(b.label)),
		[facetOptions.countries],
	);
	const stateOptions = useMemo<FacetOption[]>(() => {
		if (!filters.country) {
			return [];
		}
		const country = filters.country;
		return statesForCountry(facetOptions, country)
			.map((id) => ({
				id,
				label: State.getStateByCodeAndCountry(id, country)?.name ?? id,
			}))
			.sort((a, b) => a.label.localeCompare(b.label));
	}, [facetOptions, filters.country]);
	const cityOptions = useMemo<FacetOption[]>(
		() =>
			citiesForLocation(facetOptions, filters.country, filters.state).map(
				(id) => ({ id, label: id }),
			),
		[facetOptions, filters.country, filters.state],
	);

	const isInitiallyEmpty = totalCount === 0;

	const hrefForPage = (target: number): string => {
		if (isServerMode) {
			return target === 1 ? "/therapists" : `/therapists/page/${target}`;
		}
		const next = new URLSearchParams(params.toString());
		if (target === 1) {
			next.delete("page");
		} else {
			next.set("page", String(target));
		}
		const qs = next.toString();
		// Server-mode links stay fragment-free for clean indexable URLs; filtered
		// pagination re-fetches in place, so the anchor keeps the scroll position.
		return qs ? `/therapists?${qs}#results` : "/therapists#results";
	};

	return (
		<div
			className={cn(
				"mx-auto w-full max-w-6xl px-4 py-10 sm:px-6 sm:py-14 lg:py-16",
				aside != null && "xl:max-w-[88rem]",
			)}
		>
			<header className="mb-8 flex flex-col gap-3 sm:mb-10">
				<h1 className="font-semibold text-3xl text-klarify-neutral-900 tracking-tight sm:text-4xl">
					<Trans>Find a therapist</Trans>
				</h1>
				<p className="max-w-2xl text-base text-klarify-neutral-600 leading-relaxed">
					<Trans>
						Browse therapists hosting their public profile on Klarify. Search by
						name or filter by where they practice.
					</Trans>
				</p>
			</header>

			{!isInitiallyEmpty ? (
				<div className="mb-6 flex flex-col gap-3 rounded-2xl border border-klarify-neutral-200 bg-white/70 p-3 shadow-[0_1px_3px_rgba(0,0,0,0.04)] backdrop-blur-sm sm:mb-8 sm:p-4">
					<div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-3">
						<div className="relative flex w-full items-center sm:flex-1">
							<Input
								isSearch
								noMaxWidth
								maxLength={200}
								value={queryDraft}
								onChange={(e) => setQueryDraft(e.target.value)}
								placeholder={t`Search by name, location, or what they help with`}
								aria-label={t`Search therapists`}
								inputClassName="rounded-xl pr-10 text-klarify-neutral-800 placeholder:text-klarify-neutral-400 focus-visible:border-klarify-forest-300 focus-visible:ring-2 focus-visible:ring-klarify-forest-200"
							/>
							{queryDraft ? (
								<Button
									type="button"
									variant="ghost"
									size="icon"
									rounding="default"
									onClick={() => setQueryDraft("")}
									aria-label={t`Clear search`}
									className="absolute right-2 h-6 w-6 text-klarify-neutral-400 hover:bg-klarify-neutral-100 hover:text-klarify-neutral-700"
								>
									<X className="h-3.5 w-3.5" />
								</Button>
							) : null}
						</div>
						<div className="flex shrink-0 items-center gap-2 px-1 sm:px-0">
							<Button
								type="button"
								variant="outline"
								rounding="md"
								onClick={() => setFiltersExpanded((v) => !v)}
								aria-expanded={filtersExpanded}
								aria-controls="directory-filter-options"
								className="h-11 gap-2 rounded-xl bg-white px-4 font-medium text-klarify-neutral-700 hover:border-klarify-neutral-300 hover:bg-klarify-neutral-50 hover:text-klarify-neutral-700"
							>
								<SlidersHorizontal className="h-4 w-4 text-klarify-neutral-500" />
								<span>
									<Trans>Filters</Trans>
								</span>
								{activeFilterCount > 0 ? (
									<span className="ml-0.5 inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-klarify-forest-700 px-1.5 font-semibold text-[11px] text-white">
										{activeFilterCount}
									</span>
								) : null}
								<ChevronDown
									className={cn(
										"h-4 w-4 text-klarify-neutral-400 transition-transform",
										filtersExpanded ? "rotate-180" : "",
									)}
								/>
							</Button>
						</div>
					</div>
					{filtersExpanded ? (
						<fieldset
							id="directory-filter-options"
							className="flex min-w-0 flex-col gap-2 border-0 p-0"
						>
							<legend className="sr-only">
								<Trans>Therapist filters</Trans>
							</legend>
							<div className="grid grid-cols-1 gap-2 md:grid-cols-3">
								<TherapistDirectoryFacetPicker
									mode="single"
									placeholder={t`Country`}
									searchPlaceholder={t`Search countries…`}
									options={countryOptions}
									value={filters.country}
									onChange={onCountryChange}
								/>
								<TherapistDirectoryFacetPicker
									mode="single"
									placeholder={
										filters.country && stateOptions.length === 0
											? t`No regions`
											: t`State / province`
									}
									searchPlaceholder={t`Search regions…`}
									options={stateOptions}
									value={filters.state}
									disabled={!filters.country || stateOptions.length === 0}
									onChange={onStateChange}
								/>
								<TherapistDirectoryFacetPicker
									mode="single"
									placeholder={t`City`}
									searchPlaceholder={t`Search cities…`}
									options={cityOptions}
									value={filters.city}
									disabled={!filters.country || cityOptions.length === 0}
									onChange={onCityChange}
								/>
							</div>
							<div className="flex flex-col gap-2 md:flex-row md:items-center md:gap-3">
								<div className="md:max-w-xs md:flex-1">
									<TherapistDirectoryFacetPicker
										mode="multi"
										placeholder={t`Specialty`}
										searchPlaceholder={t`Search specialties…`}
										options={SPECIALTY_OPTIONS}
										value={filters.specialties}
										onChange={onSpecialtyChange}
									/>
								</div>
								<div className="flex flex-wrap gap-2">
									{SESSION_FORMAT_LIST.map(({ id, label, Icon }) => {
										const active = filters.formats.includes(id);
										return (
											<Button
												key={id}
												type="button"
												variant="outline"
												rounding="md"
												onClick={() => toggleFormat(id)}
												aria-pressed={active}
												className={cn(
													"gap-2 px-3 py-2",
													active
														? "border-klarify-forest-700 bg-klarify-forest-700 text-white hover:border-klarify-forest-800 hover:bg-klarify-forest-800 hover:text-white"
														: "bg-klarify-neutral-50 text-klarify-neutral-800 hover:border-klarify-neutral-300 hover:bg-klarify-neutral-50 hover:text-klarify-neutral-800",
												)}
											>
												<Icon
													className={cn(
														"h-4 w-4",
														active ? "text-white" : "text-klarify-forest-700",
													)}
												/>
												<span className="font-medium">{label}</span>
											</Button>
										);
									})}
								</div>
							</div>
						</fieldset>
					) : null}
					{hasFilters ? (
						<div className="flex items-center justify-end px-1 pt-1">
							<Button
								type="button"
								variant="link"
								size="sm"
								onClick={clearAll}
								className="h-auto p-0 font-medium text-klarify-forest-700 text-xs"
							>
								<Trans>Clear filters</Trans>
							</Button>
						</div>
					) : null}
				</div>
			) : null}

			{page === 1 && isServerMode && featured && featured.length > 0 ? (
				<FeaturedTherapistsRow entries={featured} />
			) : null}

			<div
				className={cn(
					aside != null &&
						"xl:grid xl:grid-cols-[minmax(0,1fr)_320px] xl:items-start xl:gap-8",
				)}
			>
				<div className="min-w-0">
					<div id="results" />

					{!isInitiallyEmpty ? (
						<DirectorySortBar
							count={viewTotal}
							sort={filters.sort}
							onSortChange={onSortChange}
						/>
					) : null}

					{isInitiallyEmpty ? (
						<EmptyState
							title={t`No therapists yet`}
							description={t`We're just getting started. Therapists are setting up their profiles, check back soon.`}
						/>
					) : isSearching ? (
						<TherapistDirectoryCardPlaceholderGrid />
					) : searchFailed ? (
						<EmptyState
							title={t`Something went wrong`}
							description={t`We couldn't load results. Please try again.`}
							action={
								<Button
									type="button"
									variant="outline"
									onClick={() => search.refetch()}
									className="mt-4"
								>
									<Trans>Try again</Trans>
								</Button>
							}
						/>
					) : viewEntries.length === 0 ? (
						<EmptyState
							title={t`No therapists match your search`}
							description={t`Try clearing a filter or searching for a different name or location.`}
							action={
								<Button
									type="button"
									variant="outline"
									onClick={clearAll}
									className="mt-4"
								>
									<Trans>Clear filters</Trans>
								</Button>
							}
						/>
					) : split ? (
						<>
							{/* State filter active: in-person primaries first, remote matches grouped below. Pagination intentionally omitted — state result sets are bounded. */}
							<ul className="grid grid-cols-1 gap-4 sm:gap-5 md:grid-cols-2 lg:grid-cols-3">
								{split.inPerson.map((entry) => (
									<li key={entry.href}>
										<TherapistDirectoryCard {...entry} />
									</li>
								))}
							</ul>
							{split.remote.length > 0 ? (
								<section className="mt-10">
									<h2 className="mb-4 font-medium text-klarify-neutral-500 text-sm uppercase tracking-wider">
										<Trans>Also available remotely</Trans>
									</h2>
									<ul className="grid grid-cols-1 gap-4 sm:gap-5 md:grid-cols-2 lg:grid-cols-3">
										{split.remote.map((entry) => (
											<li key={entry.href}>
												<TherapistDirectoryCard {...entry} />
											</li>
										))}
									</ul>
								</section>
							) : null}
							{viewEntries.length < viewTotal ? (
								<p className="mt-6 text-center text-klarify-neutral-500 text-sm">
									<Trans>Showing the first {viewEntries.length} results.</Trans>
								</p>
							) : null}
						</>
					) : (
						<>
							<ul className="grid grid-cols-1 gap-4 sm:gap-5 md:grid-cols-2 lg:grid-cols-3">
								{viewEntries.map((entry) => (
									<li key={entry.href}>
										<TherapistDirectoryCard {...entry} />
									</li>
								))}
							</ul>
							<TherapistDirectoryPagination
								page={viewPage}
								totalPages={viewTotalPages}
								hrefForPage={hrefForPage}
							/>
						</>
					)}
				</div>
				{aside != null ? (
					// xl:mt-[45px] = sort bar 21px + its mb-6, so the rail's heading
					// lines up with the first card row instead of the sort bar.
					<aside className="mt-10 xl:sticky xl:top-24 xl:mt-[45px] xl:self-start">
						{aside}
					</aside>
				) : null}
			</div>
		</div>
	);
};

const DirectorySortBar = ({
	count,
	sort,
	onSortChange,
}: {
	count: number;
	sort: SortOption;
	onSortChange: (value: SortOption) => void;
}) => {
	const { t } = useLingui();
	return (
		<div className="mb-6 flex items-center justify-between gap-3">
			<p className="text-klarify-neutral-500 text-sm">
				{t({
					message: linguiPlural(
						{ count: count },
						{ one: "# therapist", other: "# therapists" },
					),
				})}
			</p>
			<div className="flex items-center gap-2">
				<span className="text-klarify-neutral-500 text-sm">
					<Trans>Sort</Trans>
				</span>
				<Select
					value={sort}
					onValueChange={(v) => {
						if (isSortOption(v)) {
							onSortChange(v);
						}
					}}
				>
					<SelectTrigger className="h-auto min-w-0 gap-1 border-0 bg-transparent px-0 py-0 font-medium text-klarify-neutral-900 text-sm shadow-none hover:text-klarify-forest-700">
						<SelectValue />
					</SelectTrigger>
					<SelectContent>
						<SelectItem value="recommended">
							<Trans context="directory sort option">Recommended</Trans>
						</SelectItem>
						<SelectItem value="name">
							<Trans>Name (A–Z)</Trans>
						</SelectItem>
						<SelectItem value="newest">
							<Trans>Newest</Trans>
						</SelectItem>
					</SelectContent>
				</Select>
			</div>
		</div>
	);
};
DirectorySortBar.displayName = "DirectorySortBar";

const EmptyState = ({
	title,
	description,
	action,
}: {
	title: string;
	description?: string;
	action?: React.ReactNode;
}) => (
	<div className="mx-auto flex max-w-md flex-col items-center rounded-2xl border border-klarify-neutral-200 border-dashed bg-white/40 px-8 py-16 text-center">
		<h2 className="font-semibold text-klarify-neutral-900 text-lg tracking-tight">
			{title}
		</h2>
		{description ? (
			<p className="mt-2 text-klarify-neutral-600 text-sm leading-relaxed">
				{description}
			</p>
		) : null}
		{action}
	</div>
);
